import { Button, Grid, MenuItem, SvgIcon, TextField } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UndetailedBrandingsResponse } from '../../../app/services/api/generated';
import { Edit05 } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { ThemeContext } from '../../../theme/context/themeProviderContext';

interface BrandingSelectorProps {
  franchiseId: number;
  franchiseName: string;
}

const DEFAULT_THEME = '0';

const currentBrandingId = (): string => {
  try {
    const stored = localStorage.getItem('branding');
    return stored ? String(JSON.parse(stored).id) : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

/**
 * Franchise branding is a specialist setting: it selects a brand pack rather than a scalar value,
 * so it keeps its own control next to the catalogue rather than a row in it. Same permission gate
 * as before the catalogue rewrite: FranchiseDynamicBrandingWrite plus the FranchiseDynamicBranding
 * configuration, both checked by the page.
 */
export const BrandingSelector = ({ franchiseId, franchiseName }: BrandingSelectorProps) => {
  const { t } = useTranslation('manageConfigurations');
  const navigate = useNavigate();
  const { logError } = useLogError();
  const { isOrderlyAdmin } = useContext(UserContext);
  const [, changeBranding] = useContext(ThemeContext);
  const { getAllBranding, getAllBrandingForFranchise, getBrandingForFranchise, updateSelectedBrandingForFranchise } = useNswagClient();
  const [brandings, setBrandings] = useState<UndetailedBrandingsResponse[]>([]);
  const [selected, setSelected] = useState<string>(currentBrandingId);

  useEffect(() => {
    const load = isOrderlyAdmin() ? getAllBranding() : getAllBrandingForFranchise(franchiseId);
    void load
      .then(result => setBrandings(result?.data ?? []))
      .catch(logError);
  }, [franchiseId]);

  const handleChange = (brandingId: string) => {
    setSelected(brandingId);
    if (brandingId === DEFAULT_THEME) localStorage.removeItem('branding');

    void updateSelectedBrandingForFranchise(Number(brandingId), franchiseId)
      .then(() => getBrandingForFranchise(franchiseId))
      .then(result => {
        if (result?.data) changeBranding(result.data);
      })
      .catch(logError);
  };

  return (
    <Grid
      container
      spacing={3}
      alignItems="center"
    >
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextField
          select
          fullWidth
          value={selected}
          label={`${t('selectABrandingChange')} ${franchiseName} ${t('franchise')}`}
          onChange={event => handleChange(event.target.value)}
          sx={{ mb: 0 }}
        >
          <MenuItem value={DEFAULT_THEME}>{t('branding.defaultTheme')}</MenuItem>
          {brandings.map(branding => (
            <MenuItem
              key={branding.id}
              value={String(branding.id)}
            >
              {branding.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item>
        <Button
          variant="primary"
          size="lg"
          startIcon={(
            <SvgIcon>
              <Edit05 />
            </SvgIcon>
          )}
          onClick={() => navigate('/manage-configurations/branding')}
        >
          {t('manageBrandPack')}
        </Button>
      </Grid>
    </Grid>
  );
};
